import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DownloadAppDialog } from '@/components/DownloadAppDialog';

/**
 * Thirrsty.com brand logo.
 *
 * Modes:
 *  - Default: renders as a <Link to="/"> (returns the user to home).
 *  - asDownloadTrigger=true: renders as a <button> that opens the
 *    DownloadAppDialog when tapped, and shows the "Tap to Download the
 *    Mobile App" hint label to the LEFT of the mark. Used on public /
 *    marketing pages only (Splash, Login, Signup, Pricing, Contact,
 *    Privacy, Terms) — NOT on authenticated in-app pages.
 */
export function Logo({
  size = 128,
  withText = true,
  asDownloadTrigger = false,
  hintLabel = 'Tap to Download the Mobile App',
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const imgAndWordmark = (
    <>
      <img
        src="/logo.png"
        alt="Thirrsty.com logo"
        width={size}
        height={size}
        className="rounded-xl object-cover"
        style={{ width: size, height: size }}
        data-testid="app-logo-image"
      />
      {withText && (
        <span
          className="font-display text-4xl font-semibold tracking-tight th-gold-shine"
          style={{ fontFamily: 'Space Grotesk' }}
          data-testid="app-logo-wordmark"
        >
          Thirrsty.com
        </span>
      )}
    </>
  );

  if (asDownloadTrigger) {
    return (
      <>
        {/* Mobile-only prominent hint ABOVE the logo, with a down-arrow */}
        <div
          className="sm:hidden mb-2 flex flex-col items-center gap-0.5 select-none"
          data-testid="app-logo-download-hint-mobile"
        >
          <span className="text-[12px] uppercase tracking-[0.24em] text-[#F6B24A] font-bold">
            Tap the logo
          </span>
          <span className="text-[14px] text-white font-semibold">
            to download the Mobile App
          </span>
          <svg
            className="h-5 w-5 text-[#F6B24A] animate-bounce"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M12 4 V19 M5 13 L12 20 L19 13"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </div>

        <div className="flex items-center gap-3" data-testid="app-logo-download-trigger-wrapper">
          {/* Desktop hint label to the LEFT of the logo */}
          <div
            className="hidden sm:flex flex-col items-end text-right leading-tight select-none flex-shrink-0 whitespace-nowrap"
            aria-hidden
            data-testid="app-logo-download-hint"
          >
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#F6B24A] font-bold">
              Tap
            </span>
            <span className="text-[12px] text-white font-semibold">
              to Download<br />the Mobile App
            </span>
            <svg
              className="mt-0.5 h-3 w-8 text-[#F6B24A]"
              viewBox="0 0 24 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M0 5 H21"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M18 1 L23 5 L18 9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>

          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl transition-transform duration-150 hover:scale-[1.03] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B24A]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            aria-label={hintLabel}
            data-testid="app-logo-download-button"
          >
            {imgAndWordmark}
          </button>
        </div>

        <DownloadAppDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      </>
    );
  }

  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2"
      data-testid="app-logo-home-link"
    >
      {imgAndWordmark}
    </Link>
  );
}

export default Logo;
